import React, { useMemo, useRef, memo, useState } from 'react';
import { useSelector } from 'react-redux';
import { mergeWith } from 'lodash';
import { Widgets, widgetsWithGridView, widgetWithDatasource } from '../../WidgetLibraryGen2.jsx';
import WidgetHeader from './WidgetHeader.lazy.jsx';
import exportAsImage from '../../../common/exportAsImage.jsx';
import styles from './Widget.module.scss';
import DatasourceEngineGrid from './DatasourceEngineGrid.jsx';
import DatasourceV2Provider from '../../datasource_v2/DatasourceV2Provider.jsx';
import { selectUsesDatasourceV2 } from '../../datasource_v2/selectors.js';

/**
 * Widget component.
 *
 * @param {Object} props
 * @param {number} props.widgetId - Project id
 * @param {boolean} props.editable - Whether the widget is editable or not (dashboard in edit mode)
 * @param {boolean} props.isEditing - Whether current widget is being edited
 *
 * @returns {JSX.Element} Widget component
 */
function Widget({ widgetId, editable, isEditing, widgetHeight, permissions, additionalFeatures, ...rest }) {
  const [displayGrid, setDisplayGrid] = useState(false);
  const widget = useSelector(state => state.dashboard.widgets[widgetId]);
  const layoutComponent = useSelector(state => state.dashboard.layout[widgetId]);
  const {background, ...globalTheme} = useSelector(state => state.dashboard.theme);
  const gridView = widget.settings.gridView || {};

  const type = widget.widget_type;
  const exportRef = useRef();

  const mergedTheme = useMemo(() => {
    return mergeWith({}, globalTheme, widget.theme, (objValue, srcValue) => {
      // Using global theme if widget theme value set as null
      if (srcValue === null) return objValue;
    });
  }, [widget.theme, globalTheme]);

  const params = useMemo(() => {
    const params = mergedTheme.params || {};
    return {
      ...params,
      ...(params.textColor && { color: params.textColor })
    };
  }, [mergedTheme.params]);


  const padding = useMemo(() => {
    const p = mergedTheme?.overrides?.common?.padding || {};
    return {
      ...({ '--widget-padding-top': `${p.top ?? 10}px` }),
      ...({ '--widget-padding-bottom': `${p.bottom ?? 10}px` }),
      ...({ '--widget-padding-left': `${p.left ?? 10}px` }),
      ...({ '--widget-padding-right': `${p.right ?? 10}px` }),
    }
  }, [mergedTheme])


  const onExportAsImage = () => {
    exportAsImage(exportRef.current, `widget-${widget.id}`)
  };

  const gridMode = gridView.position ?? 'toggleable';

  const viewGrid = useMemo(() => {
    const isValidWidget = widgetsWithGridView.includes(type);
    const hasFixedPosition = gridMode !== 'toggleable';
    return isValidWidget && gridView.enabled && (hasFixedPosition || displayGrid);
  }, [type, gridView.enabled, gridMode, displayGrid]);

  const hasDatasource = widgetWithDatasource.includes(type);
  const usesDatasourceV2 = useSelector(selectUsesDatasourceV2(widgetId));
  const hasGridView = widgetsWithGridView.includes(type);

  const WidgetComponent = useMemo(() => Widgets[type], [type]);
  const baseProps = useMemo(() => ({
    widgetId: widgetId,
    widgetRef: exportRef,
    theme: mergedTheme,
    editing: isEditing,
    widgetHeight: widgetHeight,
    editable,
    permissions,
    additionalFeatures
  }), [widgetId, exportRef, mergedTheme, isEditing, editable, permissions, additionalFeatures]);

  const Component = useMemo(() => {
    const widgetEl = <WidgetComponent {...baseProps} />;

    if (!hasDatasource) return widgetEl;

    if (usesDatasourceV2) {
      return <DatasourceV2Provider widgetId={widgetId}>{widgetEl}</DatasourceV2Provider>;
    }

    // All datasource widgets are wrapped in DatasourceEngineGrid.
    // This provides a single AG Grid instance as the filter engine + context provider.
    // canDisplay=false keeps the grid hidden for widgets not in widgetsWithGridView.
    return (
      <DatasourceEngineGrid
        widgetId={widgetId}
        displayGrid={viewGrid}
        gridMode={gridMode}
        gridSize={gridView.size}
        canDisplay={hasGridView}
      >
        {widgetEl}
      </DatasourceEngineGrid>
    );
  }, [hasDatasource, usesDatasourceV2, hasGridView, viewGrid, gridMode, widgetId, gridView.size, WidgetComponent, baseProps]);

  return (
    <div className='widget-grid-item gen2-widget' data-id={layoutComponent.i} id={widget.id}>
      <div className='widget-container noselect'>
        <div ref={exportRef} className={`card dashboard-widget ${styles['dashboard-widget']} thin-scrollbar ${styles[type] || ''} ${type}`} style={{...params, ...padding}} >
          <WidgetHeader
            widget={widget}
            editable={editable}
            theme={mergedTheme}
            onExportAsImage={onExportAsImage}
            editing={isEditing}
            displayGrid={displayGrid}
            setDisplayGrid={setDisplayGrid}
            gridView={gridView}
          />
          {Component}
        </div>
      </div>
    </div>
  );
};

export default memo(Widget);
